import { useTranslation } from 'react-i18next'

import { SiteGroupsApi } from '@/services/targeting/targetingLookupApi'
import type { ObjectRef } from '@/services/targeting/targeting'
import { ObjectPicker } from '../ObjectPicker'
import { NegateToggle } from './NegateToggle'

/**
 * The targeting types whose selection is a list of other objects. Publishers,
 * sites and ad units are picked in the inventory browser instead, so only site
 * groups are left here.
 */
export type ObjectTargetType = 'siteGroup'

export interface ObjectTargetValue {
  items: ObjectRef[]
  negated: boolean
}

export interface ObjectTargetEditorProps {
  type: ObjectTargetType
  value: ObjectTargetValue
  onChange: (value: ObjectTargetValue) => void
}

/** Picks the site groups a line item targets. */
export function ObjectTargetEditor({ type, value, onChange }: ObjectTargetEditorProps) {
  const { t } = useTranslation()
  const what = t(`targeting.types.${type}.plural`).toLowerCase()

  return (
    <div className="flex flex-col gap-4">
      <NegateToggle
        negated={value.negated}
        what={what}
        onChange={(negated) => onChange({ ...value, negated })}
      />
      <ObjectPicker
        queryKey={`targeting-${type}`}
        search={(term) => SiteGroupsApi.getAll(term)}
        selected={value.items}
        onChange={(items) => onChange({ ...value, items })}
        addLabel={t('targeting.addType', { what })}
        searchPlaceholder={t('targeting.searchType', { what })}
        emptyText={t('targeting.noneSelected', { what })}
        renderMeta={(item) => item.description}
      />
    </div>
  )
}
